import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { portalService } from "apiServices";
import { Buttons, Displays, Modals } from "components";
import { AlertControl } from "components/Modals/Alert";
import { IMG_PATH } from "utils/constants/imgPath";
import {
  BOOKING_SOURCE,
  CREATED_BY,
  RENDER_BOOKING_SOURCE,
  RENDER_SCHOOL,
  RENDER_SEAT_BOOKING_ERROR,
  RENDER_SEAT_STATUS,
  SEAT_STATUS,
} from "utils/functions/seatBooking";
import AdminBookingModal from "../../Modals/AdminBookingModal";
import BookingDetailModal from "../../Modals/BookingDetailModal";
import ReasonModal from "../../Modals/ReasonModal";

const LEGEND = [SEAT_STATUS.FREE, SEAT_STATUS.LOCKED, SEAT_STATUS.PENDING, SEAT_STATUS.TAKEN, SEAT_STATUS.BLOCKED];
// แอดมินจอง/กันได้เฉพาะที่ว่างหรือที่กันไว้ (ล็อกที่หมดเวลา API ส่งมาเป็นว่างแล้ว)
const BOOKABLE = [SEAT_STATUS.FREE, SEAT_STATUS.BLOCKED];

const SeatTab = ({ activityId, schools, config = {}, canUpdate }) => {
  const [_isLoading, _setIsLoading] = useState(true);
  const [_isBgLoading, _setIsBgLoading] = useState(false);
  const [_data, _setData] = useState({ seats: [], stats: {} });
  const [_zone, _setZone] = useState();
  const [_selected, _setSelected] = useState([]);
  const [_modal, _setModal] = useState(null);

  const _fetchData = useCallback(async () => {
    const res = await portalService.GET_SEAT_BOOKING_SEATS(activityId);
    if (res && res.status === 200) _setData(res.data);
    _setIsLoading(false);
  }, [activityId]);

  useEffect(() => {
    _fetchData();
  }, [_fetchData]);

  const zones = useMemo(() => {
    const result = {};
    _data.seats.forEach((s) => {
      const zone = (result[s.zone] = result[s.zone] || { zone: s.zone, total: 0, free: 0 });
      zone.total += 1;
      if (s.status === SEAT_STATUS.FREE) zone.free += 1;
    });
    // เรียงตามลำดับชื่อโซนที่ตั้งไว้ (เหมือนฝั่งลูกค้า) โซนที่ไม่ได้ตั้งชื่อต่อท้ายตามรหัส
    const order = (config.zone_names || []).map((z) => z.zone);
    const rank = (zone) => (order.includes(zone) ? order.indexOf(zone) : order.length);
    return Object.values(result).sort(
      (a, b) => rank(a.zone) - rank(b.zone) || a.zone.localeCompare(b.zone, undefined, { numeric: true })
    );
  }, [_data.seats, config.zone_names]);
  const zoneNameOf = (zone) => ((config.zone_names || []).find((z) => z.zone === zone) || {}).name;

  const currentZone = _zone || (zones[0] && zones[0].zone);
  const seatById = useMemo(() => new Map(_data.seats.map((s) => [s.seat_id, s])), [_data.seats]);
  const selectedSeats = _selected.map((id) => seatById.get(id)).filter(Boolean);

  const _toggleSeat = (seat) =>
    _setSelected((prev) =>
      prev.includes(seat.seat_id) ? prev.filter((id) => id !== seat.seat_id) : [...prev, seat.seat_id]
    );

  const _afterChange = async () => {
    _setSelected([]);
    _setModal(null);
    await _fetchData();
  };

  const _renderTitle = (seat) => {
    const lines = [`${seat.seat_id} · ${RENDER_SEAT_STATUS(seat.status).label}`];
    if (seat.booking_code) lines.push(`รหัสจอง ${seat.booking_code}`);
    if (seat.booker_name) lines.push(`ผู้จอง ${seat.booker_name}`);
    if (seat.viewer_name) lines.push(`ชื่อผู้ชม ${seat.viewer_name}`);
    if (seat.school_code && seat.school_code !== "GUEST") lines.push(`โรงเรียน ${RENDER_SCHOOL(seat.school_code)}`);
    if (seat.booking_source) {
      lines.push(
        `${RENDER_BOOKING_SOURCE(seat.booking_source)} · ${
          seat.created_by_type === CREATED_BY.ADMIN ? "แอดมินจองให้" : "ลูกค้าจองเอง"
        }`
      );
    }
    if (seat.blocked_note) lines.push(`หมายเหตุ ${seat.blocked_note}`);
    return lines.join("\n");
  };

  const _handleBlock = async (note) => {
    _setIsBgLoading(true);
    const res = await portalService.POST_SEAT_BOOKING_SEAT_BLOCK(activityId, {
      seat_ids: selectedSeats.filter((s) => s.status === SEAT_STATUS.FREE).map((s) => s.seat_id),
      note,
    });
    _setIsBgLoading(false);
    if (res && res.status === 200) {
      const { blocked, failed } = res.data;
      toast.success(`กันที่นั่งแล้ว ${blocked.length} ที่`);
      if (failed.length) toast.error(`กันไม่ได้ (ไม่ว่างแล้ว): ${failed.join(", ")}`);
      _afterChange();
    } else {
      toast.error(RENDER_SEAT_BOOKING_ERROR(res, "กันที่นั่งไม่สำเร็จ"));
    }
  };

  const _handleRelease = () => {
    const seatIds = selectedSeats.filter((s) => s.status === SEAT_STATUS.BLOCKED).map((s) => s.seat_id);
    AlertControl.show({
      title: "ปล่อยที่นั่ง",
      description: `ปล่อย ${seatIds.length} ที่นั่งที่กันไว้ให้กลับเป็นว่างใช่หรือไม่`,
      btnLabel1: "ยกเลิก",
      btnLabel2: "ยืนยัน",
      onEvent2: async () => {
        _setIsBgLoading(true);
        const res = await portalService.POST_SEAT_BOOKING_SEAT_RELEASE(activityId, { seat_ids: seatIds });
        _setIsBgLoading(false);
        if (res && res.status === 200) {
          toast.success(`ปล่อยที่นั่งแล้ว ${res.data.released} ที่`);
          _afterChange();
        } else {
          toast.error(RENDER_SEAT_BOOKING_ERROR(res, "ปล่อยที่นั่งไม่สำเร็จ"));
        }
      },
    });
  };

  const freeCount = selectedSeats.filter((s) => s.status === SEAT_STATUS.FREE).length;
  const blockedCount = selectedSeats.filter((s) => s.status === SEAT_STATUS.BLOCKED).length;
  const stats = _data.stats || {};

  if (_isLoading) return <Displays.Loading theme_standard_loading heightLayout="360px" />;
  if (!_data.seats.length) {
    return <div className="empty_block">ยังไม่มีผังที่นั่ง นำเข้าได้ที่แท็บ "ตั้งค่า"</div>;
  }

  return (
    <div>
      <Displays.BGLoading visible={_isBgLoading} />
      <div className="stat_row">
        <div className="stat_item">
          <div className="stat_label">ว่าง</div>
          <div className="stat_value">{stats.free || 0}</div>
        </div>
        <div className="stat_item">
          <div className="stat_label">ลูกค้ากำลังจอง</div>
          <div className="stat_value">{stats.locked || 0}</div>
        </div>
        <div className="stat_item">
          <div className="stat_label">รอยืนยัน</div>
          <div className="stat_value">{stats.pending || 0}</div>
        </div>
        <div className="stat_item">
          <div className="stat_label">จองแล้ว</div>
          <div className="stat_value">{stats.taken || 0}</div>
        </div>
        <div className="stat_item">
          <div className="stat_label">กันไว้</div>
          <div className="stat_value">{stats.blocked || 0}</div>
        </div>
        <div className="stat_item">
          <div className="stat_label">โควตาออนไลน์ใช้ไป</div>
          <div className="stat_value">
            {_data.online_used || 0}
            {_data.online_quota ? ` / ${_data.online_quota}` : ""}
          </div>
        </div>
      </div>

      {/* รูปผังฮอลล์ให้แอดมินเห็นว่าโซนอยู่ตรงไหน กดกรอบโซนบนรูปเพื่อสลับโซนได้ */}
      {config.seat_map_image ? (
        <div className="seat_map_preview">
          <img src={IMG_PATH + config.seat_map_image} alt="seat map" />
          {(config.zone_areas || []).map((area, index) => (
            <div
              key={`${area.zone}-${index}`}
              className={`map_area${area.zone === currentZone ? " active" : ""}`}
              style={{ left: `${area.x}%`, top: `${area.y}%`, width: `${area.w}%`, height: `${area.h}%` }}
              title={`โซน ${area.zone}`}
              onClick={() => _setZone(area.zone)}
            />
          ))}
        </div>
      ) : (
        <div className="hint" style={{ marginBottom: 10 }}>
          ยังไม่มีรูปผังฮอลล์ อัปโหลดและวาดกรอบโซนได้ที่แท็บตั้งค่า
        </div>
      )}

      <div className="chip_row">
        {zones.map((z) => (
          <div
            key={z.zone}
            className={`chip${z.zone === currentZone ? " active" : ""}`}
            onClick={() => _setZone(z.zone)}
          >
            โซน {z.zone}
            {zoneNameOf(z.zone) ? ` ${zoneNameOf(z.zone)}` : ""} · ว่าง {z.free}/{z.total}
          </div>
        ))}
      </div>
      <div className="legend_row">
        {LEGEND.map((status) => (
          <div className="legend_item" key={status}>
            <span className="box" style={{ background: RENDER_SEAT_STATUS(status).color }} />
            {RENDER_SEAT_STATUS(status).label}
          </div>
        ))}
        <div className="legend_item">
          <span className="box" style={{ background: "#4b1e78" }} />
          เลือกอยู่
        </div>
      </div>
      <div className="hint" style={{ marginBottom: 10 }}>
        คลิกที่นั่งว่าง/กันไว้เพื่อเลือก (เลือกได้หลายที่ ข้ามโซนได้) · วางเมาส์ดูรหัสจอง/ผู้จอง · คลิกที่นั่งที่มีการจองเพื่อดูรายละเอียด
      </div>

      <Displays.SeatGrid
        seats={_data.seats.filter((s) => s.zone === currentZone)}
        selected={_selected}
        isSelectable={(seat) => canUpdate && BOOKABLE.includes(seat.status)}
        onToggle={_toggleSeat}
        onClickSeat={(seat) =>
          seat.booking_ref_id && _setModal({ type: "detail", bookingId: seat.booking_ref_id })
        }
        renderTitle={_renderTitle}
      />

      {selectedSeats.length > 0 && (
        <div className="action_bar">
          <div>
            เลือก {selectedSeats.length} ที่: {_selected.join(", ")}
          </div>
          <div className="action_btns">
            <div className="w200">
              <Buttons.BgStandard
                theme_green
                label="จองให้ลูกค้า (จ่ายเงิน)"
                onClick={() => _setModal({ type: "book", source: BOOKING_SOURCE.PAID })}
              />
            </div>
            <div className="w160">
              <Buttons.BgStandard
                theme_purple
                label="ภายใน / VIP"
                onClick={() => _setModal({ type: "book", source: BOOKING_SOURCE.INTERNAL })}
              />
            </div>
            <div className="w140">
              <Buttons.BgStandard
                label="กันที่นั่ง"
                disabled={!freeCount}
                onClick={() => _setModal({ type: "block" })}
              />
            </div>
            <div className="w140">
              <Buttons.BgStandard label="ปล่อยที่นั่ง" disabled={!blockedCount} onClick={_handleRelease} />
            </div>
            <div className="w100">
              <Buttons.BgStandard theme_gray label="ล้าง" onClick={() => _setSelected([])} />
            </div>
          </div>
        </div>
      )}

      <Modals.BodyEmpty isShowModal={!!_modal} onCloseModal={() => _setModal(null)}>
        {_modal && _modal.type === "book" && (
          <AdminBookingModal
            activityId={activityId}
            seatIds={_selected}
            source={_modal.source}
            schools={schools}
            onClose={() => _setModal(null)}
            onSuccess={_afterChange}
          />
        )}
        {_modal && _modal.type === "block" && (
          <ReasonModal
            title={`กันที่นั่ง ${freeCount} ที่ (ซ่อนจากลูกค้า)`}
            label="หมายเหตุ (ไม่บังคับ) เช่น เก็บไว้ให้แขก VIP"
            confirmLabel="กันที่นั่ง"
            onClose={() => _setModal(null)}
            onConfirm={_handleBlock}
          />
        )}
        {_modal && _modal.type === "detail" && (
          <BookingDetailModal bookingId={_modal.bookingId} onClose={() => _setModal(null)} />
        )}
      </Modals.BodyEmpty>
    </div>
  );
};

export default SeatTab;
